(() => {
  const ROOT = '[data-tcle-mega-menu]';
  const DESKTOP_BREAKPOINT = 990;

  function getTriggers(root) {
    return Array.from(root.querySelectorAll('.tcle-mega-menu__trigger'));
  }

  function getPanelFocusables(panel) {
    return Array.from(panel.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'));
  }

  function isDesktopViewport() {
    return window.innerWidth >= DESKTOP_BREAKPOINT;
  }

  function syncTrigger(detail) {
    const summary = detail.querySelector('.tcle-mega-menu__trigger, .tcle-mega-menu__mobile-trigger');
    const panel = summary?.nextElementSibling;
    if (!summary) return;
    summary.setAttribute('aria-expanded', detail.hasAttribute('open') ? 'true' : 'false');
    if (panel?.id) summary.setAttribute('aria-controls', panel.id);
  }

  function closeItem(item) {
    item.removeAttribute('open');
    syncTrigger(item);
  }

  function openItem(root, item) {
    item.setAttribute('open', '');
    syncTrigger(item);
    closeSiblings(root, item);
    positionPanel(item);
  }

  function closeSiblings(root, current) {
    root.querySelectorAll('.tcle-mega-menu__top[open]').forEach((item) => {
      if (item !== current) closeItem(item);
    });
  }

  function closeAll(root) {
    root.querySelectorAll('.tcle-mega-menu__top[open]').forEach(closeItem);
  }

  function focusFirstLink(detail) {
    const panel = detail.querySelector('.tcle-mega-menu__panel');
    const target = panel ? getPanelFocusables(panel)[0] : null;
    target?.focus();
  }

  function focusLastLink(detail) {
    const panel = detail.querySelector('.tcle-mega-menu__panel');
    const focusables = panel ? getPanelFocusables(panel) : [];
    focusables[focusables.length - 1]?.focus();
  }

  function focusRelativeInPanel(panel, current, direction) {
    const focusables = getPanelFocusables(panel);
    const index = focusables.indexOf(current);
    if (index === -1 || !focusables.length) return;
    const nextIndex = (index + direction + focusables.length) % focusables.length;
    focusables[nextIndex]?.focus();
  }

  function positionPanel(detail) {
    const panel = detail.querySelector('.tcle-mega-menu__panel');
    const headerWrapper = document.querySelector('.section-header, .header-wrapper');
    if (!panel || !headerWrapper) return;
    const rect = headerWrapper.getBoundingClientRect();
    const top = Math.max(rect.bottom + 8, 0);
    const maxHeight = Math.max(window.innerHeight - top - 24, 320);
    panel.style.setProperty('--tcle-mega-menu-top', `${top}px`);
    panel.style.setProperty('--tcle-mega-menu-max-height', `${maxHeight}px`);
  }

  function initDesktop(root) {
    const items = root.querySelectorAll('.tcle-mega-menu__top');

    items.forEach((item, index) => {
      const summary = item.querySelector('.tcle-mega-menu__trigger');
      const panel = item.querySelector('.tcle-mega-menu__panel');
      if (!summary || !panel) return;

      const summaryText = summary.querySelector('span')?.textContent?.trim() || `Menu ${index + 1}`;
      if (!summary.id) summary.id = `TCLEMegaMenuTrigger-${index + 1}`;
      if (!panel.id) panel.id = `TCLEMegaMenuPanel-${index + 1}`;

      summary.setAttribute('aria-haspopup', 'true');
      summary.setAttribute('id', summary.id);
      panel.setAttribute('role', 'group');
      panel.setAttribute('aria-labelledby', summary.id);
      panel.setAttribute('aria-label', `${summaryText} submenu`);
      syncTrigger(item);

      item.addEventListener('toggle', () => {
        syncTrigger(item);
        if (item.hasAttribute('open')) {
          closeSiblings(root, item);
          positionPanel(item);
        }
      });

      item.addEventListener('focusout', () => {
        requestAnimationFrame(() => {
          if (!item.contains(document.activeElement)) closeItem(item);
        });
      });

      summary.addEventListener('click', () => {
        if (!isDesktopViewport()) return;
        requestAnimationFrame(() => {
          if (item.hasAttribute('open')) {
            closeSiblings(root, item);
            positionPanel(item);
          }
        });
      });

      summary.addEventListener('keydown', (event) => {
        const ordered = getTriggers(root);
        const triggerIndex = ordered.indexOf(summary);

        if (event.key === 'Escape') {
          closeItem(item);
          summary.focus();
          return;
        }

        if (event.key === 'ArrowRight') {
          event.preventDefault();
          ordered[(triggerIndex + 1) % ordered.length]?.focus();
        }

        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          ordered[(triggerIndex - 1 + ordered.length) % ordered.length]?.focus();
        }

        if (event.key === 'Home') {
          event.preventDefault();
          ordered[0]?.focus();
        }

        if (event.key === 'End') {
          event.preventDefault();
          ordered[ordered.length - 1]?.focus();
        }

        if (event.key === 'ArrowDown') {
          event.preventDefault();
          if (!item.hasAttribute('open')) openItem(root, item);
          focusFirstLink(item);
        }

        if (event.key === 'ArrowUp') {
          event.preventDefault();
          if (!item.hasAttribute('open')) openItem(root, item);
          focusLastLink(item);
        }
      });

      panel.addEventListener('keydown', (event) => {
        const active = document.activeElement;

        if (event.key === 'Escape') {
          event.preventDefault();
          closeItem(item);
          summary.focus();
        }

        if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && active && panel.contains(active)) {
          event.preventDefault();
          focusRelativeInPanel(panel, active, event.key === 'ArrowDown' ? 1 : -1);
        }

        if (event.key === 'Home') {
          event.preventDefault();
          focusFirstLink(item);
        }

        if (event.key === 'End') {
          event.preventDefault();
          focusLastLink(item);
        }
      });
    });

    document.addEventListener('click', (event) => {
      if (!root.contains(event.target)) closeAll(root);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeAll(root);
    });

    window.addEventListener('resize', () => {
      root.querySelectorAll('.tcle-mega-menu__top[open]').forEach(positionPanel);
    });

    window.addEventListener('scroll', () => {
      root.querySelectorAll('.tcle-mega-menu__top[open]').forEach(positionPanel);
    }, { passive: true });
  }

  function initMobile() {
    document.querySelectorAll('.tcle-mega-menu__mobile-item > details').forEach((detail, index) => {
      const summary = detail.querySelector('.tcle-mega-menu__mobile-trigger');
      const panel = detail.querySelector('.tcle-mega-menu__mobile-panel');
      if (!summary || !panel) return;

      if (!summary.id) summary.id = `TCLEMobileMegaMenuTrigger-${index + 1}`;
      if (!panel.id) panel.id = `TCLEMobileMegaMenuPanel-${index + 1}`;
      summary.setAttribute('aria-haspopup', 'true');
      panel.setAttribute('role', 'group');
      panel.setAttribute('aria-labelledby', summary.id);
      syncTrigger(detail);

      detail.addEventListener('toggle', () => {
        syncTrigger(detail);
        if (detail.hasAttribute('open')) {
          document.querySelectorAll('.tcle-mega-menu__mobile-item > details[open]').forEach((openItem) => {
            if (openItem !== detail) closeItem(openItem);
          });
        }
      });

      summary.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          closeItem(detail);
          summary.focus();
        }
      });
    });
  }

  function relocateCallout() {
    const callout = document.querySelector('[data-tcle-callout-bar]');
    const barSlot = document.querySelector('.utility-bar .localization-wrapper');
    if (callout && barSlot) barSlot.appendChild(callout);
  }

  document.addEventListener('DOMContentLoaded', () => {
    relocateCallout();
    document.querySelectorAll(ROOT).forEach(initDesktop);
    initMobile();
  });
})();
